import { Fragment, useEffect, useId, useState, type ReactNode } from 'react';
import { useApp, type NavScope, type PageId } from '../store/AppContext';
import { HeaderSearch } from './HeaderSearch';
import { LaneIcon } from '../components/LaneIcon';
import { FarmStatus, NotificationBell } from './HeaderWidgets';
import { NAV_GROUPS } from './navGroups';

const BRAND_ICONS = {
  bing: '/brand/bing.png',
  google: '/brand/google.png',
} as const;

const NEXT_MENU: Record<NavScope, NavScope> = { bing: 'google', google: 'bing' };
const MENU_NAMES: Record<NavScope, string> = { bing: 'Bing', google: 'Google' };

function BrandIcon({ scope }: { scope: NavScope }) {
  return <img src={BRAND_ICONS[scope]} alt="" width={44} height={44} />;
}

function BrandSwitch({
  scope,
  className,
  onClick,
}: {
  scope: NavScope;
  className: string;
  onClick: () => void;
}) {
  const next = NEXT_MENU[scope];
  const label = `Показать меню ${MENU_NAMES[next]}`;
  const arrowId = useId();
  return (
    <div className={className}>
      <button type="button" className="brand-switch" title={label} aria-label={label} onClick={onClick}>
        <svg className="brand-swap" viewBox="0 0 64 64" aria-hidden="true">
          <defs>
            <marker id={arrowId} viewBox="0 0 10 10" refX="5" refY="5" markerWidth="3.2" markerHeight="3.2" orient="auto">
              <path d="M0 0L10 5L0 10z" fill="currentColor" stroke="none" />
            </marker>
          </defs>
          <path d="M5.69 22.42A28 28 0 0 1 58.31 22.42" markerEnd={`url(#${arrowId})`} />
          <path d="M58.31 41.58A28 28 0 0 1 5.69 41.58" markerEnd={`url(#${arrowId})`} />
        </svg>
        <BrandIcon scope={scope} />
      </button>
    </div>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const { hasPermission, page, ui, theme, goto, setUi, toggleTheme, logout } = useApp();

  const isActive = (scope: NavScope, target: PageId) =>
    ui.scope === scope && page === target ? 'active' : undefined;

  const subnav = (scope: NavScope, target: PageId) => {
    if (ui.scope !== scope || page !== target || (target !== 'search' && target !== 'launch' && target !== 'settings')) return null;
    const entries = target === 'search'
      ? [['overview', 'Общая статистика'], ['positions', 'Позиции сайтов']] as const
      : target === 'launch'
        ? [['autopilot', 'Автозапуск'], ['scenarios', 'Сценарии'], ['manual', 'Ручной запуск']] as const
        : [['nodes', 'Ноды'], ['telegram', 'Telegram'], ['password', 'Пароль'], ['reset', 'Сброс']] as const;
    return <div className="nav-sublist">{entries.map(([id, label]) => (
      <button key={id} type="button" className={ui.subpage === id ? 'active' : undefined}
        aria-current={ui.subpage === id ? 'page' : undefined}
        onClick={() => { closeDrawer(); setUi({ subpage: id }); }}>
        {label}
      </button>
    ))}</div>;
  };

  const navGroups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => hasPermission(`${item.page}.view`)),
  })).filter((group) => group.items.length > 0);

  const [navOpen, setNavOpen] = useState(false);
  const [menu, setMenu] = useState<NavScope>(ui.scope);

  useEffect(() => {
    setMenu(ui.scope);
  }, [ui.scope]);

  const cycleMenu = () => setMenu((m) => NEXT_MENU[m]);

  const closeDrawer = () => setNavOpen(false);
  const narrowNav = () => window.matchMedia('(max-width: 1000px)').matches;

  useEffect(() => {
    if (!navOpen) return undefined;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') closeDrawer();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [navOpen]);

  useEffect(() => {
    closeDrawer();
  }, [page]);

  const isDark = theme === 'dark';

  return (
    <div id="app">
      <div className={`shell${navOpen ? ' nav-open' : ''}`}>
        {navOpen ? (
          <button type="button" className="nav-scrim" aria-label="Закрыть меню" onClick={closeDrawer} />
        ) : null}
        <aside className={`sidebar menu-${menu}`} id="sideNav">
          <div className="side-track">
            <div className="side-panel side-bing">
              <div className="bnav">
                <BrandSwitch scope="bing" className="nav-brands" onClick={cycleMenu} />
                <nav className="nav" id="nav">
                  {navGroups.map((group) => (
                    <Fragment key={group.title}>
                      <div className="nav-group">{group.title}</div>
                      {group.items.map((item) => (
                        <Fragment key={item.page}><button
                          type="button"
                          data-page={item.page}
                          className={isActive('bing', item.page)}
                          onClick={() => {
                            closeDrawer();
                            goto(item.page, 'bing');
                          }}
                        >
                          {item.lane ? <LaneIcon kind={item.lane} /> : null}
                          {item.label}
                        </button>{subnav('bing', item.page)}</Fragment>
                      ))}
                    </Fragment>
                  ))}
                </nav>

                <div className="side-foot-actions">
                  {hasPermission('settings.view') ? (
                  <Fragment>
                  <button
                    type="button"
                    data-page="settings"
                    className={isActive('bing', 'settings')}
                    onClick={() => {
                      closeDrawer();
                      goto('settings', 'bing');
                    }}
                  >
                    <LaneIcon kind="settings" />
                    Общие настройки
                  </button>
                  {subnav('bing', 'settings')}
                  </Fragment>
                  ) : null}
                  <button type="button" className="nav-logout" onClick={() => void logout()}>
                    <LaneIcon kind="logout" />
                    Выход
                  </button>
                </div>
              </div>
            </div>

            <div className="side-panel side-google">
              <div className="gnav locked">
                <BrandSwitch scope="google" className="gnav-brands" onClick={cycleMenu} />
                <button type="button" className="gnav-lock" aria-label="Пока недоступно. Показать меню Bing" onClick={cycleMenu}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="5" y="11" width="14" height="10" rx="2" />
                    <path d="M8 11V7.5a4 4 0 0 1 8 0V11" />
                    <path d="M12 15v2" />
                  </svg>
                  <span>Пока недоступно</span>
                </button>
                <nav className="gnav-list" inert>
                  {navGroups.map((group) => (
                    <div key={group.title} className={`gnav-group tone-${group.tone}`}>
                      <div className="gnav-title">{group.title}</div>
                      {group.items.map((item) => (
                        <Fragment key={item.page}><button
                          type="button"
                          className={isActive('google', item.page)}
                          onClick={() => {
                            closeDrawer();
                            goto(item.page, 'google');
                          }}
                        >
                          {item.lane ? <LaneIcon kind={item.lane} /> : null}
                          {item.label}
                        </button>{subnav('google', item.page)}</Fragment>
                      ))}
                    </div>
                  ))}
                </nav>
                <div className="gnav-group gnav-foot tone-green" inert>
                  {hasPermission('settings.view') ? (
                  <button
                    type="button"
                    className={isActive('google', 'settings')}
                    onClick={() => {
                      closeDrawer();
                      goto('settings', 'google');
                    }}
                  >
                    <LaneIcon kind="settings" />
                    Общие настройки
                  </button>
                  ) : null}
                  <button type="button" className="gnav-logout" onClick={() => void logout()}>
                    <LaneIcon kind="logout" />
                    Выход
                  </button>
                </div>
              </div>
            </div>
          </div>
        </aside>

        <div className="content">
          <header className="appbar">
            <div className="ab-left">
              <button
                className="waffle"
                id="navToggle"
                type="button"
                aria-label="Меню"
                aria-expanded={navOpen}
                aria-controls="nav"
                onClick={() => {
                  if (!narrowNav()) return;
                  setNavOpen((open) => !open);
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M4 7h16M4 12h16M4 17h16" />
                </svg>
              </button>
            </div>

            <HeaderSearch />

            <div className="ab-right">
              <FarmStatus />
              <NotificationBell />
              <button
                className="ab-icon"
                id="themeToggle"
                type="button"
                title={isDark ? 'Светлая тема' : 'Тёмная тема'}
                aria-label={isDark ? 'Включить светлую тему' : 'Включить тёмную тему'}
                aria-pressed={isDark}
                onClick={toggleTheme}
              >
                <svg className="theme-sun" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                  <circle cx="12" cy="12" r="4" />
                  <path d="M12 2.5v2.2M12 19.3v2.2M2.5 12h2.2M19.3 12h2.2M5.05 5.05l1.55 1.55M17.4 17.4l1.55 1.55M18.95 5.05l-1.55 1.55M6.6 17.4l-1.55 1.55" />
                </svg>
                <svg className="theme-moon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 14.5A8.5 8.5 0 1 1 9.5 4 7 7 0 0 0 20 14.5Z" />
                </svg>
              </button>
            </div>
          </header>
          <main>{children}</main>
        </div>
      </div>
    </div>
  );
}
